import { Button, Col, FormInstance, Row, Spin, Table, message, Collapse } from "antd";
import React, { RefObject } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import {
  APIError,
  AssetAllocation,
  Quarter,
  FormFieldConfig,
  EconomicOutlook,
} from "../helpers/shared-interfaces";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { AxiosResponse } from "axios";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import {
  DELETE_ASSET_ALLOCATION,
  GET_QUARTER,
  UPDATE_QUARTER,
} from "../gql/economicIndicators";
import moment from "moment";
import { ColumnType } from "antd/lib/table";
import ModalCreator from "../components/ModalCreator";

const CREATE_OUTLOOK = `
  mutation CreateOutlook($quarterId: ID!, $outlook: String!) {
    createEconomicOutlook(quarterId: $quarterId, outlook: $outlook) {
      economicOutlook {
        id
        outlook
      }
      errors {
        property
        description
      }
    }
  }
`;

const UPDATE_OUTLOOK = `
  mutation UpdateOutlook($quarterId: ID!, $outlookId: ID!, $outlook: String!) {
    updateEconomicOutlook(outlookId: $outlookId, quarterId: $quarterId, outlook: $outlook) {
      economicOutlook {
        id
        outlook
      }
      errors {
        property
        description
      }
    }
  }
`;

const DELETE_OUTLOOK = `
  mutation DeleteOutlook($id: ID!) {
    deleteEconomicOutlook(id: $id) {
      result
      errors {
        property
        description
      }
    }
  }
`;

interface RouteParams {
  id: string;
}

interface State {
  id: string;
  name: string;
  isLoading: boolean;
  quarter?: Quarter;
  assetAllocations?: AssetAllocation[];
  economicOutlooks?: EconomicOutlook[];
  showOutlookModal: boolean;
  isOutlookLoading: boolean;
  showEditOutlookModal: boolean;
  selectedOutlook?: EconomicOutlook;
}

export function getAssetAllocationColumns<T extends AssetAllocation>(
  deleteAssetAllocation: (id: number) => void,
): ColumnType<T>[] {
  return [
    {
      title: "Asset",
      dataIndex: "asset",
      key: "asset",
      render(text: string, record: AssetAllocation): JSX.Element {
        return <span>{record?.assetAllocationItem?.name}</span>;
      },
    },
    {
      title: "Rate Type",
      dataIndex: "rateType",
      key: "rateType",
      render(text: string, record: AssetAllocation): JSX.Element {
        return <span>{record?.assetAllocationRate?.name}</span>;
      },
    },
    {
      title: "Scheme",
      dataIndex: "scheme",
      key: "scheme",
      render(text: string, record: AssetAllocation): JSX.Element {
        return <span>{record?.product?.name}</span>;
      },
    },
    {
      title: "Enable",
      dataIndex: "enable",
      key: "enable",
      render(text: string, record: AssetAllocation): JSX.Element {
        return <span>{record?.enable?.toString()}</span>;
      },
    },
    {
      title: "Rate %",
      dataIndex: "rate%",
      key: "rate%",
      render(text: string, record: AssetAllocation): JSX.Element {
        return <span>{record?.rate}</span>;
      },
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      render(text: string, record: AssetAllocation): JSX.Element {
        return <span>{record?.order}</span>;
      },
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      width: "8%",
      render(text: string, record: AssetAllocation): JSX.Element {
        return (
          <NavLink
            to={{
              pathname: `/quarters/${record?.quarter?.id}/asset-allocations/${record.id}`,
              state: { record },
            }}
          >
            <EditOutlined />
          </NavLink>
        );
      },
    },
    {
      title: "Delete",
      dataIndex: "Delete",
      key: "Delete",
      width: "8%",
      render(text: string, record: AssetAllocation): JSX.Element {
        return (
          <Button onClick={() => deleteAssetAllocation(record?.id)}>
            <DeleteOutlined />
          </Button>
        );
      },
    },
  ];
}

export function getOutlookColumns<T extends EconomicOutlook>(
  deleteOutlook: (id: number) => void,
  editOutlook: (outlook: EconomicOutlook) => void,
): ColumnType<T>[] {
  return [
    {
      title: "Outlook",
      dataIndex: "outlook",
      key: "outlook",
      render(text: string, record: EconomicOutlook): JSX.Element {
        return <span>{record.outlook}</span>;
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render(text: string, record: EconomicOutlook): JSX.Element {
        return <span>{moment(record.createdAt).format('DD/MM/YYYY HH:mm')}</span>;
      },
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      width: "8%",
      render(text: string, record: EconomicOutlook): JSX.Element {
        return (
          <Button onClick={() => editOutlook(record)}>
            <EditOutlined />
          </Button>
        );
      },
    },
    {
      title: "Delete",
      dataIndex: "delete",
      key: "delete",
      width: "8%",
      render(text: string, record: EconomicOutlook): JSX.Element {
        return (
          <Button onClick={() => deleteOutlook(record.id)}>
            <DeleteOutlined />
          </Button>
        );
      },
    },
  ];
}

export default class EditQuarter extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  outlookReference: RefObject<FormInstance>;
  editOutlookReference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      id: "",
      name: "",
      isLoading: true,
      showOutlookModal: false,
      isOutlookLoading: false,
      showEditOutlookModal: false,
    };
    this.reference = React.createRef<FormInstance>();
    this.outlookReference = React.createRef<FormInstance>();
    this.editOutlookReference = React.createRef<FormInstance>();
    this.getQuarter = this.getQuarter.bind(this);
    this.deleteAssetAllocation = this.deleteAssetAllocation.bind(this);
    this.updateQuarter = this.updateQuarter.bind(this);
    this.redirectToNewAssetAllocationPage =
      this.redirectToNewAssetAllocationPage.bind(this);
    this.toggleOutlookModal = this.toggleOutlookModal.bind(this);
    this.createOutlook = this.createOutlook.bind(this);
    this.deleteOutlook = this.deleteOutlook.bind(this);
    this.editOutlook = this.editOutlook.bind(this);
    this.toggleEditOutlookModal = this.toggleEditOutlookModal.bind(this);
    this.updateOutlook = this.updateOutlook.bind(this);
  }

  async componentDidMount(): Promise<void> {
    this.setState({ id: this.props.match.params.id }, () => {
      this.getQuarter();
    });
  }

  redirectToNewAssetAllocationPage = (): void => {
    const { id } = this.state;
    this.props.history.push(`/quarters/${id}/new-asset-allocation?id=${id}`);
  };

  getQuarter = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_QUARTER,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const quarter = data.data?.quarter;
            this.setState({
              quarter,
              assetAllocations: quarter?.assetAllocations,
              economicOutlooks: quarter?.economicOutlooks || [],
            });
          } else {
            message.error("Failed to get quarter data");
          }
        });
      },
    );
  };

  deleteAssetAllocation = (id?: number): void => {
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_ASSET_ALLOCATION,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false });
            if (!data.errors && data.data?.deleteAssetAllocation?.result) {
              message.success("Asset Allocation deleted successfuly");
              this.getQuarter();
            } else {
              message.error("Failed to delete asset allocation");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false });
          });
      },
    );
  };

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  redirectToViewQuarterPage(): void {
    this.props.history.push(`/quarters/${this.state.id}`);
  }

  async updateQuarter(): Promise<void> {
    const { id } = this.state;
    interface Response {
      data?: {
        updateQuarter: {
          quarter: Quarter;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "startDate",
        "endDate",
        "name",
        "cediDepreciation",
        "gdp",
        "inflation",
        "tbill91",
        "tbill182",
        "tbill365",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_QUARTER,
        variables: {
          id,
          ...data,
        },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateQuarter",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Quarter updated sucessfully");
        this.redirectToViewQuarterPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to update quarter - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  toggleOutlookModal = (): void => {
    const prevState = this.state.showOutlookModal;
    this.setState({ showOutlookModal: !prevState });
  };

  createOutlook = async (): Promise<void> => {
    if (!this.outlookReference.current) return;
    interface Response {
      economicOutlook: string;
      errors: APIError[];
    }

    try {
      const data = await this.outlookReference.current.validateFields(['outlook']);
      
      const response: AxiosResponse<Response> = await request({
        query: CREATE_OUTLOOK,
        variables: {
          quarterId: this.state.id,
          outlook: data.outlook,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createEconomicOutlook",
      );

      this.setState({ isOutlookLoading: false });

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.outlookReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else if (mutation) {
        message.success("Outlook created successfully");
        this.toggleOutlookModal();
        this.getQuarter();
      }
    } catch (error) {
      this.setState({ isOutlookLoading: false });
      message.error(`Failed to create outlook - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  deleteOutlook = (id: number): void => {
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: DELETE_OUTLOOK,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false });
            if (!data.errors && data.data?.deleteEconomicOutlook?.result) {
              message.success("Outlook deleted successfully");
              this.getQuarter();
            } else {
              message.error("Failed to delete outlook");
            }
          })
          .catch((): void => {
            this.setState({ isLoading: false });
          });
      },
    );
  };

  editOutlook = (outlook: EconomicOutlook): void => {
    this.setState({
      selectedOutlook: outlook,
      showEditOutlookModal: true,
    });
  };

  toggleEditOutlookModal = (): void => {
    const prevState = this.state.showEditOutlookModal;
    this.setState({ 
      showEditOutlookModal: !prevState,
      selectedOutlook: prevState ? undefined : this.state.selectedOutlook,
    });
  };

  updateOutlook = async (): Promise<void> => {
    if (!this.editOutlookReference.current || !this.state.selectedOutlook) return;
    interface Response {
      economicOutlook: EconomicOutlook;
      errors: APIError[];
    }

    try {
      const data = await this.editOutlookReference.current.validateFields(['outlook']);
      
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_OUTLOOK,
        variables: {
          outlookId: this.state.selectedOutlook.id,
          outlook: data.outlook,
          quarterId: this.state.id
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateEconomicOutlook",
      );

      this.setState({ isOutlookLoading: false });

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.editOutlookReference.current.setFields(err.map(mapAPIErrorToFormError));
      } else if (mutation) {
        message.success("Outlook updated successfully");
        this.toggleEditOutlookModal();
        this.getQuarter();
      }
    } catch (error) {
      this.setState({ isOutlookLoading: false });
      message.error(`Failed to update outlook - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  render(): JSX.Element | null {
    const { 
      isLoading, 
      quarter, 
      assetAllocations, 
      economicOutlooks,
      showOutlookModal, 
      isOutlookLoading,
      showEditOutlookModal,
      selectedOutlook,
    } = this.state;
    const fields: FormFieldConfig[] = [
      {
        id: "name",
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        initialValue: quarter?.name,
        placeholder: "Name",
        colConfig: {
          span: 7,
        },
      },
      {
        id: "startDate",
        name: "startDate",
        rule: "object",
        inputType: "defaultDate",
        required: true,
        label: "Start Date",
        initialValue: quarter?.startDate ? moment(quarter?.startDate) : "",
        placeholder: "Start Date",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "endDate",
        name: "endDate",
        rule: "object",
        inputType: "defaultDate",
        required: true,
        label: "End Date",
        placeholder: "End Date",
        initialValue: quarter?.endDate ? moment(quarter?.endDate) : "",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "cediDepreciation",
        name: "cediDepreciation",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Cedi Depreciation",
        placeholder: "Cedi Depreciation",
        initialValue: quarter?.cediDepreciation,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "gdp",
        name: "gdp",
        rule: "number",
        inputType: "number",
        required: false,
        label: "GDP",
        placeholder: "GDP",
        initialValue: quarter?.gdp,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "inflation",
        name: "inflation",
        rule: "number",
        inputType: "number",
        required: false,
        label: "Inflation",
        placeholder: "Inflation",
        initialValue: quarter?.inflation,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "tbill91",
        name: "tbill91",
        rule: "number",
        inputType: "number",
        required: false,
        label: "T-Bill 91",
        placeholder: "T-Bill 91",
        initialValue: quarter?.tbill91,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "tbill182",
        name: "tbill182",
        rule: "number",
        inputType: "number",
        required: false,
        label: "T-Bill 182",
        placeholder: "T-Bill 182",
        initialValue: quarter?.tbill182,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "tbill365",
        name: "tbill365",
        rule: "number",
        inputType: "number",
        required: false,
        label: "T-Bill 365",
        placeholder: "T-Bill 365",
        initialValue: quarter?.tbill365,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "updateQuarter",
        name: "updateCategoryButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateQuarter,
      },
    ];

    return (
      <Card header="UPDATE QUARTER">
        <>
          <div>
            <Spin spinning={isLoading}>
              <BaseForm
                layout="vertical"
                reference={this.reference}
                fieldsToHide={[]}
                fields={fields}
              />
            </Spin>
          </div>
          <div>
            <Row>
              <Col>
                <p
                  style={{
                    marginTop: "20px",
                    fontSize: "15px",
                  }}
                >
                  <b>Asset Allocations</b>
                </p>
              </Col>
              <Col span={20} style={{ textAlign: "right" }}>
                <EntitledSection
                  entitlement={entitlements.EconomicIndicators.create}
                >
                  <Button
                    className="base-button"
                    size="large"
                    data-testid="create-asset-allocation-button"
                    onClick={this.redirectToNewAssetAllocationPage}
                    style={{ marginRight: '10px' }}
                  >
                    <PlusOutlined />
                    Add Asset Allocation
                  </Button>
                  <Button
                    className="base-button"
                    size="large"
                    data-testid="create-outlook-button"
                    onClick={this.toggleOutlookModal}
                  >
                    <PlusOutlined />
                    Add Outlook
                  </Button>
                </EntitledSection>
              </Col>
            </Row>

            <Collapse
              style={{
                marginBottom: "12px",
              }}
            >
              <Collapse.Panel key="assetAllocations" header="Asset Allocations">
                {assetAllocations && (
                  <Table
                    scroll={{ x: 400 }}
                    columns={getAssetAllocationColumns(this.deleteAssetAllocation)}
                    dataSource={assetAllocations}
                  />
                )}
              </Collapse.Panel>
            </Collapse>
          </div>
          <div>
            <Collapse
              style={{
                marginBottom: "12px",
              }}
            >
              <Collapse.Panel key="outlooks" header="Economic Outlooks">
                {economicOutlooks && (
                  <Table
                    scroll={{ x: 400 }}
                    columns={getOutlookColumns(this.deleteOutlook, this.editOutlook)}
                    dataSource={economicOutlooks}
                    rowKey="id"
                  />
                )}
              </Collapse.Panel>
            </Collapse>
          </div>
          {showOutlookModal &&
            ModalCreator({
              title: "Add Outlook",
              okType: "primary",
              okText: "Create",
              isLoading: isOutlookLoading,
              showModal: showOutlookModal,
              showFooter: true,
              onOkay: this.createOutlook,
              toggleModal: this.toggleOutlookModal,
              inscription: (
                <Spin spinning={false}>
                  <Row>
                    <Col span={24} style={{ marginBottom: "20px" }}>
                      <div style={{ marginTop: "10px" }}>
                        <BaseForm
                          layout="vertical"
                          reference={this.outlookReference}
                          fields={[
                            {
                              id: "outlook",
                              name: "outlook",
                              inputType: "textArea",
                              required: true,
                              rule: "string",
                              placeholder: "Enter outlook",
                              label: "Outlook",
                              elementStyle: { width: "100%" },
                              colConfig: {
                                span: 24,
                              },
                            },
                          ]}
                          fieldsToHide={[]}
                        />
                      </div>
                    </Col>
                  </Row>
                </Spin>
              ),
            })}
          {showEditOutlookModal && selectedOutlook &&
            ModalCreator({
              title: "Edit Outlook",
              okType: "primary",
              okText: "Update",
              isLoading: isOutlookLoading,
              showModal: showEditOutlookModal,
              showFooter: true,
              onOkay: this.updateOutlook,
              toggleModal: this.toggleEditOutlookModal,
              inscription: (
                <Spin spinning={false}>
                  <Row>
                    <Col span={24} style={{ marginBottom: "20px" }}>
                      <div style={{ marginTop: "10px" }}>
                        <BaseForm
                          layout="vertical"
                          reference={this.editOutlookReference}
                          fields={[
                            {
                              id: "outlook",
                              name: "outlook",
                              inputType: "textArea",
                              required: true,
                              rule: "string",
                              placeholder: "Enter outlook",
                              label: "Outlook",
                              initialValue: selectedOutlook.outlook,
                              elementStyle: { width: "100%" },
                              colConfig: {
                                span: 24,
                              },
                            },
                          ]}
                          fieldsToHide={[]}
                        />
                      </div>
                    </Col>
                  </Row>
                </Spin>
              ),
            })}
        </>
      </Card>
    );
  }
}
